import React from 'react'
import useStyles from './styles'
import { ReactComponent as DefaultPractitionerImage } from 'src/images/practitioner-default.svg'
import { AppointmentDateAndTime } from '../TreatmentInfo/AppointmentDateAndTime'
import { useTranslation } from 'react-i18next'
import { useGetTreatmentTitleWithDuration } from '@hooks/useGetTreatmentTitleWithDuration'
import { useGetTreatmentInfo } from '@hooks/useGetTreatmentInfo'
import { useConfig } from '@context/ConfigurationContext'
import useClinic from '@hooks/useClinic'
import Legal from '@screens/Contact/components/Legal'
import { useChoiceState } from '@context/ChoiceContext'
import { getImageUrlWithTransformations } from 'src/utils/getImageUrl'
import { Resize } from '@dentalux/ui-library-core'

interface Props {
  patientFullName?: string
  showDataProtectionDisclaimer?: boolean
}

export const AppointmentSummary = ({ patientFullName, showDataProtectionDisclaimer }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { practitioner } = useGetTreatmentInfo()
  const { imageUrl, name } = practitioner || {}
  const { clinicId } = useConfig()
  const { selectedBookableDateAndTime } = useChoiceState()
  const { data: clinic } = useClinic(selectedBookableDateAndTime?.clinicId || clinicId)
  const { officialName } = clinic || {}
  const treatmentTitle = useGetTreatmentTitleWithDuration()

  const appointmentData = [
    { label: t('booking_success_screen_treatment'), value: treatmentTitle },
    { label: t('booking_success_screen_time'), value: <AppointmentDateAndTime /> },
    { label: t('booking_success_screen_location'), value: officialName },
    ...(!!patientFullName ? [{ label: t('booking_success_screen_patient'), value: patientFullName }] : []),
  ]

  return (
    <div className={classes.appointmentSummary}>
      <div className={classes.separatorLine} />
      <div className={classes.appointmentInfoOuterContainer}>
        <div className={classes.appointmentInfoInnerContainer}>
          <div className={classes.practitioner}>
            <div className={classes.practitionerImageContainer}>
              {imageUrl ? (
                <img
                  className={classes.practitionerImage}
                  src={getImageUrlWithTransformations({
                    src: imageUrl,
                    quality: 'lightest',
                    transformations: [new Resize(200, 200)],
                  })}
                  alt="practitioner face"
                />
              ) : (
                <DefaultPractitionerImage className={classes.practitionerImage} />
              )}
            </div>
            <div className={classes.practitionerName}>{name}</div>
          </div>
          <div className={classes.appointmentData}>
            {appointmentData.map((data) => (
              <div className={classes.dataContainer} key={data.label}>
                <div className={classes.dataLabel}>{data.label}:</div>
                <div className={classes.dataValue}>{data.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={classes.separatorLine} />
      {showDataProtectionDisclaimer && <Legal />}
    </div>
  )
}
